import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, Badge, Spinner } from "@learn-me/ui";
import { FileText, Download } from "lucide-react";
import { formatCurrency } from "@learn-me/utils";
import {
  materialsApi,
  MATERIAL_CATEGORY_LABELS,
} from "@/features/materials/api/materialsApi";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function MaterialsCatalogPage() {
  const { data: materials, isLoading } = useQuery({
    queryKey: ["materials-catalog"],
    queryFn: () => materialsApi.listPublic(),
  });

  return (
    <div className="container py-12">
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-bold">Recursos para seguir aprendiendo</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Acceda a materiales y recursos prácticos desarrollados para
          complementar el aprendizaje y fortalecer competencias profesionales.
        </p>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : !materials || materials.length === 0 ? (
        <p className="text-center text-muted-foreground py-16">
          No hay materiales disponibles por el momento.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {materials.map((m) => {
            const list = m.listPrice != null ? Number(m.listPrice) : null;
            const sale = Number(m.salePrice);
            const showDiscount =
              !m.isFree && m.hasDiscount && list != null && list > sale;
            return (
              <Link key={m.id} to={`/materiales/${m.slug}`}>
                <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-secondary-100 relative">
                    {m.coverImageUrl ? (
                      <img
                        src={normalizeImageUrl(m.coverImageUrl) ?? ""}
                        alt={m.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <FileText className="h-14 w-14 text-secondary-400" />
                      </div>
                    )}
                    <Badge className="absolute top-3 left-3" variant="outline">
                      {MATERIAL_CATEGORY_LABELS[m.category]}
                    </Badge>
                    {showDiscount && (
                      <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0">
                        {Math.round(((list! - sale) / list!) * 100)}% OFF
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold line-clamp-2 mb-1">
                      {m.title}
                    </h3>
                    {m.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {m.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="flex items-baseline gap-2">
                        {showDiscount && (
                          <span className="text-xs text-muted-foreground line-through">
                            {formatCurrency(list!, m.currency)}
                          </span>
                        )}
                        <span className="font-bold">
                          {m.isFree
                            ? "Gratis"
                            : formatCurrency(sale, m.currency)}
                        </span>
                      </span>
                      <Download className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
